import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sun, Loader2, Send, RefreshCw, CheckCircle2, XCircle, AlertCircle, Users,
} from "lucide-react";
import { api } from "../lib/api";

function fmtDateTime(iso) {
  if (!iso) return ", ";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(start, end) {
  if (!start || !end) return ", ";
  const ms = new Date(end) - new Date(start);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

const STATUS_LABEL = {
  running: { label: "Em execução", color: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Concluído", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  completed_with_errors: { label: "Concluído c/ erros", color: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function FanoutSettings() {
  const [settings, setSettings] = useState(null);
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, l] = await Promise.all([
        api.get("/whatsapp/fanout/settings"),
        api.get("/whatsapp/fanout/last"),
      ]);
      setSettings(s.data);
      setLast(l.data.last || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar fanout.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (saving || !settings) return;
    setSaving(true);
    try {
      const r = await api.put("/whatsapp/fanout/settings", { enabled: !settings.enabled });
      setSettings(r.data);
      toast.success(r.data.enabled
        ? "Fanout ativado, envio diário às 07:00 BRT."
        : "Fanout pausado, envio diário desligado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível atualizar.");
    } finally { setSaving(false); }
  };

  const fireNow = async () => {
    if (running) return;
    if (!settings?.eligible_contacts) {
      toast.error("Nenhum contato elegível (active=true e opt-in=true). Confira a aba Contatos.");
      return;
    }
    if (!window.confirm(`Disparar fanout agora para ${settings.eligible_contacts} contato${settings.eligible_contacts === 1 ? "" : "s"}? Cada envio custa um SMS na Twilio (sandbox grátis até 9/mês).`)) return;
    setRunning(true);
    try {
      const r = await api.post("/whatsapp/fanout", {});
      const data = r.data;
      const msg = `Fanout concluído: ${data.sent} enviados, ${data.failed} falharam, ${data.skipped} pulados.`;
      data.failed > 0 ? toast.warning(msg) : toast.success(msg);
      setLast(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no disparo.");
    } finally { setRunning(false); }
  };

  return (
    <section data-testid="fanout-settings" className="bg-white border border-border rounded-2xl p-5 sm:p-6">
      <header className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-600 flex items-center gap-1.5">
            <Sun className="w-3 h-3" /> Fanout diário
          </p>
          <h3 className="font-display text-xl text-navy mt-1">Versículo do dia via WhatsApp</h3>
          <p className="text-[12px] text-navy/55 mt-1.5 max-w-md">
            Todo dia às <strong>07:00 BRT</strong>, envia o versículo + reflexão do dia para todos os
            contatos com <strong>opt-in confirmado</strong>. Pausa lateral de 500ms entre envios.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-navy/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…
        </div>
      ) : !settings ? null : (
        <div className="space-y-4">
          {/* Top stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Cell label="Contatos elegíveis" value={settings.eligible_contacts} icon={Users} ok={settings.eligible_contacts > 0} testid="fanout-eligible" />
            <Cell label="Próximo envio (auto)" value={settings.cron_utc} icon={Sun} ok={settings.enabled} testid="fanout-cron" />
            <Cell label="Delay entre envios" value={`${settings.delay_ms}ms`} icon={RefreshCw} ok testid="fanout-delay" />
          </div>

          {/* Toggle + manual fire */}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-border">
            <button
              type="button" role="switch" aria-checked={settings.enabled}
              data-testid="fanout-toggle"
              disabled={saving}
              onClick={toggle}
              className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors ${
                settings.enabled ? "bg-amber-600" : "bg-navy/20"
              } disabled:opacity-50 mt-3`}
            >
              <span className={`inline-block w-5 h-5 bg-white rounded-full transition-transform shadow ${
                settings.enabled ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
            <p className="text-sm text-navy flex-1 min-w-[160px] mt-3">
              {settings.enabled ? (
                <span><strong className="text-amber-600">Ativo:</strong> envio automático todo dia às 07:00 BRT.</span>
              ) : (
                <span className="text-navy/60"><strong>Pausado:</strong> envio automático desligado.</span>
              )}
            </p>
            <button
              type="button" onClick={fireNow}
              disabled={running || settings.eligible_contacts === 0}
              data-testid="fanout-fire-now-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white text-[12px] uppercase tracking-widest hover:bg-amber-600 disabled:opacity-50 transition-colors mt-3"
            >
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {running ? "Enviando…" : "Disparar agora"}
            </button>
          </div>

          {/* Last run */}
          <div className="border-t border-border pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Último envio</p>
              <button
                type="button" onClick={load}
                className="inline-flex items-center gap-1.5 text-[11px] text-navy/55 hover:text-navy"
                data-testid="fanout-refresh"
              >
                <RefreshCw className="w-3 h-3" /> Atualizar
              </button>
            </div>
            {!last ? (
              <p className="text-sm text-navy/55 py-4 text-center">Nenhum envio realizado ainda.</p>
            ) : (
              <LastRunCard last={last} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Cell({ label, value, icon: Icon, ok, testid }) {
  return (
    <div className="bg-[hsl(var(--parchment))] border border-border rounded-xl px-3 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-navy/50">{label}</p>
      <p className={`text-sm font-medium mt-0.5 truncate flex items-center gap-1.5 ${ok ? "text-navy" : "text-navy/40"}`}>
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? "text-emerald-600" : "text-navy/30"}`} />
        <span className="truncate">{value}</span>
      </p>
    </div>
  );
}

function LastRunCard({ last }) {
  const meta = STATUS_LABEL[last.status] || STATUS_LABEL.completed;
  return (
    <div className="bg-[hsl(var(--parchment))] border border-border rounded-xl p-4 space-y-3" data-testid="fanout-last-run">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[14px] font-medium text-navy">{last.title}</p>
          <p className="text-[12px] text-navy/55 mt-0.5 line-clamp-2">{last.body_preview}</p>
        </div>
        <span className={`inline-block text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${meta.color}`}>
          {meta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-border">
        <Stat label="Enviados" value={last.sent} color="text-emerald-700" />
        <Stat label="Falhas" value={last.failed} color={last.failed > 0 ? "text-rose-700" : "text-navy/55"} />
        <Stat label="Pulados" value={last.skipped} color="text-navy/55" />
        <Stat label="Total" value={last.total_contacts} color="text-navy" />
      </div>

      <div className="flex items-center justify-between text-[11px] text-navy/55 pt-2 border-t border-border">
        <span>Iniciado: {fmtDateTime(last.started_at)}</span>
        <span>Duração: {fmtDuration(last.started_at, last.finished_at)}</span>
        <span className="capitalize">{last.kind === "manual" ? "Manual" : "Automático"}</span>
      </div>

      {last.errors_sample && last.errors_sample.length > 0 && (
        <details className="pt-2 border-t border-border" data-testid="fanout-errors">
          <summary className="cursor-pointer text-[12px] text-rose-700 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Ver {last.errors_sample.length} erro{last.errors_sample.length > 1 ? "s" : ""} (primeiros 5)
          </summary>
          <ul className="mt-2 space-y-1.5">
            {last.errors_sample.map((e, i) => (
              <li key={i} className="text-[11px] text-navy/65 bg-white/60 rounded-md p-2">
                <span className="font-medium text-navy">{e.name || e.phone}</span>
                <span className="block text-rose-700 mt-0.5">{e.error}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, color = "text-navy" }) {
  return (
    <div className="text-center">
      <p className={`font-display text-2xl ${color}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-navy/50">{label}</p>
    </div>
  );
}
